/**
 * Zod schema for the "Register Tool" form (F032 MCP Governance) — Doc 69 Layer 2 output, imported
 * by `McpToolRegisterForm.tsx`, never redefined (form-standard.context.md §3). Rule IDs
 * `VT-01`..`VT-06` mirror the verbatim messages previously hand-rolled in `mcpServerFormFields.tsx`.
 *
 * `mcpServerId` is NOT part of this schema — it is a fixed prop (the server the caller is
 * currently viewing), never a user-editable field, matching "one form type per API command"
 * (ADR-013). It is attached to the payload after the form validates, in the wrapper component.
 */
import { z } from 'zod';
import type { DataSensitivity, McpToolRegistrationInput } from '../../../types';
import { DATA_SENSITIVITY_OPTIONS } from './mcpServerFormFields';

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

// reason: DATA_SENSITIVITY_OPTIONS is a runtime-verified non-empty array of DataSensitivity;
// zod's z.enum() requires a literal non-empty tuple type.
const DATA_SENSITIVITY_TUPLE = DATA_SENSITIVITY_OPTIONS as [DataSensitivity, ...DataSensitivity[]];

export const McpToolRegisterSchema = z.object({
  // VT-01 (required @ submit) / VT-02 (max 200 @ blur)
  toolName: z.string().trim().min(1, 'Tool name is required').max(200, 'Tool name must be 200 characters or fewer'),
  // VT-03 (optional, max 2000)
  description: z
    .string()
    .max(2000, 'Description must be 2000 characters or fewer')
    .transform((v) => v.trim() || undefined),
  // VT-04 — shared with the Register Server form's data-access-level rule (VS-11).
  dataAccessLevel: z.enum(DATA_SENSITIVITY_TUPLE, { message: 'Select a data access level' }),
  canWrite: z.boolean(),
  canDelete: z.boolean(),
  canExecuteCode: z.boolean(),
  requiresApproval: z.boolean(),
  // VT-05 (optional, valid JSON when non-empty)
  inputSchemaJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Input schema must be valid JSON' })
    .transform((v) => v || undefined),
  // VT-06 (optional, valid JSON when non-empty)
  outputSchemaJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Output schema must be valid JSON' })
    .transform((v) => v || undefined),
  // shared with VS-12 (metadata JSON)
  metadataJson: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === '' || isValidJson(v), { message: 'Metadata must be valid JSON' })
    .transform((v) => v || undefined),
});

export type McpToolRegisterFormValues = z.input<typeof McpToolRegisterSchema>;
export type McpToolRegisterFormOutput = z.output<typeof McpToolRegisterSchema>;

// Compile-time proof the schema's output + the fixed `mcpServerId` prop assembles a valid A-01
// request DTO (`mcpServerId` is intentionally excluded from the schema — see file header).
type _AssertAssignable = McpToolRegisterFormOutput & { mcpServerId: number } extends McpToolRegistrationInput
  ? true
  : false;
const _assertAssignable: _AssertAssignable = true;
void _assertAssignable;
